import { z } from 'zod';

/**
 * The two audit reads every dashboard page waits on, as far as the frontend reads them. Each
 * object passes through the keys it does not name: the check is on what a screen or a gate
 * reads, not on the whole row. Checked by `auditsApi.getAudit` and `auditsApi.getMyTeamRole`
 * through `checkResponse` (`./responseContract.ts`).
 *
 * The closed sets are the API's Prisma enums, spelled here so the seam imports nothing from
 * `src/api`; `schemas.test.ts` holds each equal to the client's own constant.
 */

/** An auditor's seat on the engagement (`AudiorTeamMemberRole`). */
export const auditorSeatSchema = z.enum([
  'lead_auditor',
  'auditor',
  'quality_assurance',
  'quality_review',
  'lead_accountant',
  'accountant',
]);

/** `AuditKind`: which seats and status presets the engagement offers. */
export const auditKindSchema = z.enum(['auditor', 'fiduciary']);

/** `AccountingStandard`. */
export const accountingStandardSchema = z.enum(['OR', 'GAAP', 'IFRS']);

/**
 * The client permission flags of an `AuditRole`, every one a non-null column. The gates in
 * `entities/audit` read them off `teamMember.role` (`hasAuditPermission`).
 */
export const auditRoleFlagsSchema = z
  .object({
    editCompany: z.boolean(),
    editAudit: z.boolean(),
    editPermissionRoles: z.boolean(),
    createNewPermissions: z.boolean(),
    manageTeam: z.boolean(),
    refetchData: z.boolean(),
    createRequests: z.boolean(),
    sensitiveRequestsWithoutAssign: z.boolean(),
    overviewAccess: z.boolean(),
    requestsAccess: z.boolean(),
    statusAccess: z.boolean(),
    documentAccess: z.boolean(),
    meetingAccess: z.boolean(),
  })
  .passthrough();

// ── GET /audit/:id/team-members/me ─────────────────────────────────────────────

/**
 * `auditTeamMember.controller.js#getMe` after `isAuditMember`: the membership that shapes the
 * top level (`member || auditor || companyAdmin`), then the caller's standing on top. What the
 * gates read is `AuditAccess` (`entities/audit/lib/auditPermissions.ts`).
 */
export const teamMemberMeSchema = z
  .object({
    /** The API's `hasAdminRights`: auditor, company admin or super-admin. */
    hasAdminPermissions: z.boolean(),
    isAuditor: z.boolean(),
    isSuperadmin: z.boolean(),
    /**
     * The role OBJECT for a client-team member, else a bare string: the auditor's seat or the
     * company admin's company role.
     */
    role: z.union([z.string(), auditRoleFlagsSchema]),
    /** The caller's auditor seat; absent without one. Gates Archive (`canArchiveAudit`). */
    auditorRole: auditorSeatSchema.optional(),
    /** The auditor membership row, `null` without a seat. */
    auditor: z
      .object({
        /** *Can import data*: a non-null column, default Yes (`canImportData`). */
        canImportData: z.boolean(),
      })
      .passthrough()
      .nullable(),
  })
  .passthrough();

export const teamMemberMeResponseSchema = z
  .object({ teamMember: teamMemberMeSchema })
  .passthrough();

// ── GET /audit/:id ──────────────────────────────────────────────────────────────

/** A user as `teamMemberUserSelect` sends one, the fields the lists and pickers draw. */
const memberUserSchema = z
  .object({
    id: z.number(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
  })
  .passthrough();

/**
 * `audit.controller.js#getOne`: the row `attachAuditById` loads (`deepAuditSelect`), through
 * `getAuditProgress`, which adds `isAuditor` (the caller's own auditor row, ABSENT without a
 * seat) and `myRole`.
 */
export const auditDetailSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    kind: auditKindSchema,
    type: z.string(),
    /** The freeze every write affordance reads (`useIsAuditArchived`). */
    archived: z.boolean(),
    pendingArchive: z.boolean(),
    archivedAt: z.string().nullable(),
    archivedBy: z.object({ firstName: z.string(), lastName: z.string() }).passthrough().nullable(),
    archiveComment: z.string().nullable(),
    archivingPeriod: z.number().nullable(),
    reportDate: z.string().nullable(),
    accountingSystem: z.string().nullable(),
    accountingStandard: accountingStandardSchema,
    bexioLastFetched: z.string().nullable(),
    financialYearStart: z.string().nullable(),
    financialYearEnd: z.string().nullable(),
    comparisonPeriodStart: z.string().nullable(),
    comparisonPeriodEnd: z.string().nullable(),
    subsequentPeriodStart: z.string().nullable(),
    subsequentPeriodEnd: z.string().nullable(),
    preset: z.object({ auditType: z.string() }).passthrough().nullable(),
    /** The caller's seat: request editing, the documents' type column, the library search. */
    isAuditor: z.object({ role: auditorSeatSchema }).passthrough().optional(),
    auditors: z.array(
      z
        .object({
          role: auditorSeatSchema,
          member: z.object({ user: memberUserSchema }).passthrough(),
        })
        .passthrough(),
    ),
    roles: z.array(
      z
        .object({
          name: z.string(),
          teamMembers: z.array(
            z
              .object({
                userId: z.number(),
                user: memberUserSchema.extend({ lastActive: z.string().nullable() }),
              })
              .passthrough(),
          ),
        })
        .passthrough(),
    ),
    company: z
      .object({
        name: z.string(),
        members: z.array(z.object({ id: z.number(), user: memberUserSchema }).passthrough()),
        /** `getOne` re-derives it from the omitted token: `'error'`, `'connected'` or absent. */
        bexioRefresh: z.enum(['error', 'connected']).optional(),
      })
      .passthrough(),
    auditorFirm: z
      .object({ id: z.number(), name: z.string(), logo: z.string().nullable() })
      .passthrough()
      .nullable(),
  })
  .passthrough();

export const auditResponseSchema = z.object({ audit: auditDetailSchema }).passthrough();
